/**
 * ADD EVENT MODAL
 * 
 * Modal for brand owners to create new events.
 * Location: src/components/events/AddEventModal.tsx
 */

import { useState, useEffect } from 'react';
import { X, Calendar, Clock, Link, Tag, Sparkles, Plus, Trash2, Repeat } from 'lucide-react';
import { onAuthStateChanged } from 'firebase/auth';
import type { User } from 'firebase/auth';
import { auth } from '../../firebase/config';
import { createEvent, updateEvent, getUserBrands } from '../../services/eventService';
import { 
  EVENT_TYPE_CONFIG, 
  type EventType, 
  type EventFormData,
  type RecurrencePattern,
  type RecurrenceConfig,
  type CalendarEvent
} from '../../types/event';
import { getUserTimezone, TIMEZONE_OPTIONS, zonedDateTimeToUtc } from '../../constants/timezones';

// Days of the week for recurring events
const WEEKDAYS = [
  { value: 0, label: 'Sun' },
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' }
];

const MONTH_WEEKS = [
  { value: 'first', label: 'First' },
  { value: 'second', label: 'Second' },
  { value: 'third', label: 'Third' },
  { value: 'fourth', label: 'Fourth' },
  { value: 'last', label: 'Last' }
];

interface AddEventModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  preselectedBrandId?: string;
  editingEvent?: CalendarEvent | null;  // Pass event to edit
}

export default function AddEventModal({ 
  isOpen, 
  onClose, 
  onSuccess,
  preselectedBrandId,
  editingEvent
}: AddEventModalProps) {
  const isEditMode = !!editingEvent;
  // Auth state (using Firebase directly)
  const [user, setUser] = useState<User | null>(null);
  
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      setUser(firebaseUser);
    });
    return () => unsubscribe();
  }, []);
  
  // User's brands they can post for
  const [userBrands, setUserBrands] = useState<Array<{
    id: string;
    name: string;
    slug: string;
    logo?: string;
    timezone?: string;
  }>>([]);
  const [loadingBrands, setLoadingBrands] = useState(true);
  
  // Form state
  const [selectedBrandId, setSelectedBrandId] = useState(preselectedBrandId || '');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [eventType, setEventType] = useState<EventType>('release');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [isAllDay, setIsAllDay] = useState(true);
  const [shopUrl, setShopUrl] = useState('');
  const [socialUrl, setSocialUrl] = useState('');
  const [discountInfo, setDiscountInfo] = useState('');
  const [productsTeased, setProductsTeased] = useState<string[]>(['']);
  
  // Recurring event state
  const [recurrencePattern, setRecurrencePattern] = useState<RecurrencePattern>('none');
  const [weeklyDays, setWeeklyDays] = useState<number[]>([]);
  const [monthlyType, setMonthlyType] = useState<'day' | 'weekday'>('day');
  const [monthlyDay, setMonthlyDay] = useState(1);
  const [monthlyWeek, setMonthlyWeek] = useState<'first' | 'second' | 'third' | 'fourth' | 'last'>('first');
  const [monthlyWeekday, setMonthlyWeekday] = useState(0);
  const [recurrenceEndDate, setRecurrenceEndDate] = useState('');
  
  // Timezone state
  const [timezone, setTimezone] = useState('');
  
  // UI state
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Load user's brands on mount
  useEffect(() => {
    async function loadBrands() {
      if (!user) return;
      
      try {
        setLoadingBrands(true);
        const brands = await getUserBrands(user.uid, user.email || '');
        setUserBrands(brands);
        
        // Auto-select if only one brand or preselected
        if (preselectedBrandId) {
          setSelectedBrandId(preselectedBrandId);
        } else if (brands.length === 1) {
          setSelectedBrandId(brands[0].id);
        }
      } catch (err) {
        console.error('Error loading brands:', err);
        setError('Failed to load your brands');
      } finally {
        setLoadingBrands(false);
      }
    }
    
    if (isOpen) {
      loadBrands();
    }
  }, [isOpen, user, preselectedBrandId]);
  
  // Set timezone when brand is selected
  useEffect(() => {
    if (selectedBrandId && userBrands.length > 0) {
      const selectedBrand = userBrands.find(b => b.id === selectedBrandId);
      if (selectedBrand?.timezone) {
        setTimezone(selectedBrand.timezone);
      } else {
        // Fall back to user's browser timezone
        setTimezone(getUserTimezone());
      }
    }
  }, [selectedBrandId, userBrands]);

  // Reset form when modal closes OR populate when editing
  useEffect(() => {
    if (!isOpen) {
      // Reset all fields when modal closes
      setTitle('');
      setDescription('');
      setEventType('release');
      setStartDate('');
      setEndDate('');
      setStartTime('');
      setEndTime('');
      setIsAllDay(true);
      setShopUrl('');
      setSocialUrl('');
      setDiscountInfo('');
      setProductsTeased(['']);
      setRecurrencePattern('none');
      setWeeklyDays([]);
      setMonthlyType('day');
      setMonthlyDay(1);
      setMonthlyWeek('first');
      setMonthlyWeekday(0);
      setRecurrenceEndDate('');
      setTimezone('');
      setError('');
    } else if (editingEvent) {
      // Populate form with existing event data
      setSelectedBrandId(editingEvent.brandId);
      setTitle(editingEvent.title);
      setDescription(editingEvent.description || '');
      setEventType(editingEvent.eventType);
      
      // Format dates for input fields (YYYY-MM-DD)
      const startDate = editingEvent.startDate.toDate();
      setStartDate(startDate.toISOString().split('T')[0]);
      
      if (editingEvent.endDate) {
        const endDate = editingEvent.endDate.toDate();
        setEndDate(endDate.toISOString().split('T')[0]);
      } else {
        setEndDate('');
      }
      
      setStartTime(editingEvent.startTime || '');
      setEndTime(editingEvent.endTime || '');
      setIsAllDay(editingEvent.isAllDay);
      setTimezone(editingEvent.timezone || '');
      setShopUrl(editingEvent.shopUrl || '');
      setSocialUrl(editingEvent.socialUrl || '');
      setDiscountInfo(editingEvent.discountInfo || '');
      setProductsTeased(editingEvent.productsTeased?.length ? editingEvent.productsTeased : ['']);
      
      // Populate recurrence settings
      if (editingEvent.recurrence && editingEvent.recurrence.pattern !== 'none') {
        setRecurrencePattern(editingEvent.recurrence.pattern);
        setWeeklyDays(editingEvent.recurrence.weeklyDays || []);
        
        if (editingEvent.recurrence.monthlyDay) {
          setMonthlyType('day');
          setMonthlyDay(editingEvent.recurrence.monthlyDay);
        } else if (editingEvent.recurrence.monthlyWeek) {
          setMonthlyType('weekday');
          setMonthlyWeek(editingEvent.recurrence.monthlyWeek);
          setMonthlyWeekday(editingEvent.recurrence.monthlyWeekday || 0);
        }
        
        if (editingEvent.recurrence.endDate) {
          const recEndDate = editingEvent.recurrence.endDate.toDate();
          setRecurrenceEndDate(recEndDate.toISOString().split('T')[0]);
        }
      } else {
        setRecurrencePattern('none');
        setWeeklyDays([]);
        setMonthlyType('day');
        setMonthlyDay(1);
        setMonthlyWeek('first');
        setMonthlyWeekday(0);
        setRecurrenceEndDate('');
      }
    }
  }, [isOpen, editingEvent]);

  // Handle adding/removing product teasers
  const addProductTeaser = () => {
    setProductsTeased([...productsTeased, '']);
  };
  
  const removeProductTeaser = (index: number) => {
    setProductsTeased(productsTeased.filter((_, i) => i !== index));
  };
  
  const updateProductTeaser = (index: number, value: string) => {
    const updated = [...productsTeased];
    updated[index] = value;
    setProductsTeased(updated);
  };

  // Form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) {
      setError('You must be logged in');
      return;
    }
    
    if (!selectedBrandId) {
      setError('Please select a brand');
      return;
    }
    
    if (!title.trim()) {
      setError('Please enter an event title');
      return;
    }
    
    if (!startDate) {
      setError('Please select a start date');
      return;
    }
    
    const selectedBrand = userBrands.find(b => b.id === selectedBrandId);
    if (!selectedBrand) {
      setError('Selected brand not found');
      return;
    }
    
    setSaving(true);
    setError('');
    
    try {
      // Parse dates as LOCAL time (not UTC)
      // new Date("2026-01-26") parses as UTC, causing off-by-one-day bugs
      const parseLocalDate = (dateStr: string): Date => {
        const [year, month, day] = dateStr.split('-').map(Number);
        return new Date(year, month - 1, day);
      };
      
      // Build recurrence config if not 'none'
      let recurrence: RecurrenceConfig | undefined;
      if (recurrencePattern !== 'none') {
        recurrence = {
          pattern: recurrencePattern,
          endDate: recurrenceEndDate ? parseLocalDate(recurrenceEndDate) : undefined
        };
        
        if (recurrencePattern === 'weekly') {
          recurrence.weeklyDays = weeklyDays.length > 0 ? weeklyDays : [parseLocalDate(startDate).getDay()];
        } else if (recurrencePattern === 'monthly') {
          if (monthlyType === 'day') {
            recurrence.monthlyDay = monthlyDay;
          } else {
            recurrence.monthlyWeek = monthlyWeek;
            recurrence.monthlyWeekday = monthlyWeekday;
          }
        }
      }
      
      const eventTz = timezone || getUserTimezone();
      const formData: EventFormData = {
        title,
        description,
        eventType,
        // Timed events: interpret the picked time in the selected timezone so the
        // stored instant is correct (otherwise "now" comparisons / reminders are
        // hours off). All-day events stay date-only.
        startDate: isAllDay
          ? parseLocalDate(startDate)
          : zonedDateTimeToUtc(startDate, startTime || '00:00', eventTz),
        endDate: endDate
          ? isAllDay
            ? parseLocalDate(endDate)
            : zonedDateTimeToUtc(endDate, endTime || startTime || '00:00', eventTz)
          : undefined,
        startTime: !isAllDay && startTime ? startTime : undefined,
        endTime: !isAllDay && endTime ? endTime : undefined,
        isAllDay,
        timezone: eventTz,  // selected timezone or fallback
        shopUrl: shopUrl || undefined,
        socialUrl: socialUrl || undefined,
        discountInfo: discountInfo || undefined,
        productsTeased: productsTeased.filter(p => p.trim()),
        recurrence
      };
      
      const brandInfo = {
        brandId: selectedBrand.id,
        brandName: selectedBrand.name,
        brandSlug: selectedBrand.slug,
        brandLogo: selectedBrand.logo
      };
      
      if (isEditMode && editingEvent?.id) {
        // Update existing event
        await updateEvent(editingEvent.id, formData, brandInfo);
      } else {
        // Create new event
        await createEvent(formData, brandInfo, user.uid);
      }
      
      onSuccess?.();
      onClose();
    } catch (err) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} event:`, err);
      setError(`Failed to ${isEditMode ? 'update' : 'create'} event. Please try again.`);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto glass-card p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-glass font-heading flex items-center gap-2">
            <Calendar className="icon-base text-pink-400" />
            {isEditMode ? 'Edit Event' : 'Add Event'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-700 rounded-lg transition-default"
          >
            <X className="icon-base text-glass-secondary" />
          </button>
        </div>

        {/* No brands message */}
        {!loadingBrands && userBrands.length === 0 && (
          <div className="text-center py-8">
            <div className="w-16 h-16 rounded-full bg-slate-700 flex items-center justify-center mx-auto mb-4">
              <Calendar className="icon-lg text-glass-secondary" />
            </div>
            <h3 className="text-lg font-bold text-glass mb-2">No Brand Pages</h3>
            <p className="text-glass-secondary mb-4">
              You need to claim a brand page before you can post events.
            </p>
            <a 
              href="/brands" 
              className="btn-primary inline-block"
            >
              Browse Brands
            </a>
          </div>
        )}

        {/* Loading state */}
        {loadingBrands && (
          <div className="text-center py-8">
            <div className="animate-spin w-8 h-8 border-2 border-pink-400 border-t-transparent rounded-full mx-auto mb-4" />
            <p className="text-glass-secondary">Loading your brands...</p>
          </div>
        )}

        {/* Form */}
        {!loadingBrands && userBrands.length > 0 && (
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Error message */}
            {error && (
              <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-3 text-red-300 text-sm">
                {error}
              </div>
            )}

            {/* Brand selector (if multiple brands) */}
            {userBrands.length > 1 && (
              <div>
                <label className="block text-sm font-medium text-glass mb-2">
                  Posting as *
                </label>
                <select
                  value={selectedBrandId}
                  onChange={(e) => setSelectedBrandId(e.target.value)}
                  className="form-input w-full"
                  required
                >
                  <option value="">Select a brand...</option>
                  {userBrands.map(brand => (
                    <option key={brand.id} value={brand.id}>
                      {brand.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Single brand display */}
            {userBrands.length === 1 && (
              <div className="bg-slate-800/60 rounded-lg p-3 flex items-center gap-3">
                {userBrands[0].logo ? (
                  <img 
                    src={userBrands[0].logo} 
                    alt="" 
                    className="w-10 h-10 rounded-lg object-cover"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-pink-500 to-purple-500 flex items-center justify-center">
                    <span className="text-white font-bold">
                      {userBrands[0].name.charAt(0)}
                    </span>
                  </div>
                )}
                <div>
                  <p className="text-sm text-glass-secondary">Posting as</p>
                  <p className="font-medium text-glass">{userBrands[0].name}</p>
                </div>
              </div>
            )}

            {/* Event type */}
            <div>
              <label className="block text-sm font-medium text-glass mb-2">
                Event Type *
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {(Object.entries(EVENT_TYPE_CONFIG) as [EventType, typeof EVENT_TYPE_CONFIG[EventType]][]).map(([type, config]) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => setEventType(type)}
                    className={`p-3 rounded-lg border-2 transition-default text-left ${
                      eventType === type
                        ? 'border-pink-500 bg-pink-500/20'
                        : 'border-slate-600 hover:border-slate-500 bg-slate-800/40'
                    }`}
                  >
                    <span className="text-lg mr-2">{config.emoji}</span>
                    <span className="text-sm font-medium text-glass">{config.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-glass mb-2">
                Event Title *
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g., Spring Collection Launch"
                className="form-input w-full"
                required
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-glass mb-2">
                Description
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Tell people what to expect..."
                rows={3}
                className="form-input w-full resize-none"
              />
            </div>

            {/* Date and Time */}
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-glass mb-2">
                  <Calendar className="icon-sm inline mr-1" />
                  Start Date *
                </label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="form-input w-full"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-glass mb-2">
                  End Date (optional)
                </label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  min={startDate}
                  className="form-input w-full"
                />
              </div>
            </div>

            {/* All day toggle */}
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={isAllDay}
                onChange={(e) => setIsAllDay(e.target.checked)}
                className="w-5 h-5 rounded border-slate-600 bg-slate-800 text-pink-500 focus:ring-pink-500"
              />
              <span className="text-glass">All day event</span>
            </label>

            {/* Time inputs (if not all day) */}
            {!isAllDay && (
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-glass mb-2">
                    <Clock className="icon-sm inline mr-1" />
                    Start Time
                  </label>
                  <input
                    type="time"
                    value={startTime}
                    onChange={(e) => setStartTime(e.target.value)}
                    className="form-input w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-glass mb-2">
                    End Time
                  </label>
                  <input
                    type="time"
                    value={endTime}
                    onChange={(e) => setEndTime(e.target.value)}
                    className="form-input w-full"
                  />
                </div>
              </div>
            )}

            {/* Timezone selector (show when not all-day) */}
            {!isAllDay && (
              <div>
                <label className="block text-sm font-medium text-glass mb-2">
                  Timezone
                </label>
                <div className="flex items-center gap-2">
                  <select
                    value={timezone}
                    onChange={(e) => setTimezone(e.target.value)}
                    className="form-input flex-grow"
                  >
                    <option value="">Select timezone...</option>
                    <optgroup label="🇺🇸 United States">
                      {TIMEZONE_OPTIONS.filter(tz => tz.value.startsWith('America/') || tz.value.startsWith('Pacific/H')).map(tz => (
                        <option key={tz.value} value={tz.value}>
                          {tz.label}
                        </option>
                      ))}
                    </optgroup>
                    <optgroup label="🌍 International">
                      {TIMEZONE_OPTIONS.filter(tz => !tz.value.startsWith('America/') && !tz.value.startsWith('Pacific/H')).map(tz => (
                        <option key={tz.value} value={tz.value}>
                          {tz.label}
                        </option>
                      ))}
                    </optgroup>
                  </select>
                  <button
                    type="button"
                    onClick={() => setTimezone(getUserTimezone())}
                    className="px-3 py-2 text-sm bg-slate-600 hover:bg-slate-500 text-glass-secondary rounded-lg transition-colors whitespace-nowrap"
                    title="Use my current timezone"
                  >
                    Detect
                  </button>
                </div>
                {timezone && (
                  <p className="text-xs text-glass-muted mt-1">
                    Times will be shown in this timezone to viewers
                  </p>
                )}
              </div>
            )}

            {/* Recurring Event */}
            <div className="border-t border-slate-700 pt-6">
              <label className="block text-sm font-medium text-glass mb-3">
                <Repeat className="icon-sm inline mr-1" />
                Repeat Event
              </label>
              
              {/* Recurrence pattern selector */}
              <div className="flex gap-2 mb-4">
                {[
                  { value: 'none', label: 'Does not repeat' },
                  { value: 'weekly', label: 'Weekly' },
                  { value: 'monthly', label: 'Monthly' }
                ].map(option => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setRecurrencePattern(option.value as RecurrencePattern)}
                    className={`px-4 py-2 rounded-lg text-sm transition-default ${
                      recurrencePattern === option.value
                        ? 'bg-purple-500 text-white'
                        : 'bg-slate-700 text-glass-secondary hover:text-glass'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
              
              {/* Weekly options */}
              {recurrencePattern === 'weekly' && (
                <div className="bg-slate-800/40 rounded-lg p-4 space-y-4">
                  <div>
                    <p className="text-sm text-glass-secondary mb-2">Repeat on:</p>
                    <div className="flex flex-wrap gap-2">
                      {WEEKDAYS.map(day => (
                        <button
                          key={day.value}
                          type="button"
                          onClick={() => {
                            if (weeklyDays.includes(day.value)) {
                              setWeeklyDays(weeklyDays.filter(d => d !== day.value));
                            } else {
                              setWeeklyDays([...weeklyDays, day.value].sort());
                            }
                          }}
                          className={`w-10 h-10 rounded-full text-sm font-medium transition-default ${
                            weeklyDays.includes(day.value)
                              ? 'bg-purple-500 text-white'
                              : 'bg-slate-700 text-glass-secondary hover:text-glass'
                          }`}
                        >
                          {day.label}
                        </button>
                      ))}
                    </div>
                    <p className="text-xs text-glass-muted mt-2">
                      Leave blank to use the day of your start date
                    </p>
                  </div>
                </div>
              )}
              
              {/* Monthly options */}
              {recurrencePattern === 'monthly' && (
                <div className="bg-slate-800/40 rounded-lg p-4 space-y-4">
                  {/* Monthly type selector */}
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setMonthlyType('day')}
                      className={`px-3 py-1.5 rounded-lg text-sm transition-default ${
                        monthlyType === 'day'
                          ? 'bg-purple-500/30 text-purple-300 border border-purple-500/50'
                          : 'bg-slate-700 text-glass-secondary'
                      }`}
                    >
                      On a specific day
                    </button>
                    <button
                      type="button"
                      onClick={() => setMonthlyType('weekday')}
                      className={`px-3 py-1.5 rounded-lg text-sm transition-default ${
                        monthlyType === 'weekday'
                          ? 'bg-purple-500/30 text-purple-300 border border-purple-500/50'
                          : 'bg-slate-700 text-glass-secondary'
                      }`}
                    >
                      On a weekday
                    </button>
                  </div>
                  
                  {/* Specific day of month */}
                  {monthlyType === 'day' && (
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-glass-secondary">On day</span>
                      <select
                        value={monthlyDay}
                        onChange={(e) => setMonthlyDay(Number(e.target.value))}
                        className="form-input w-20"
                      >
                        {Array.from({ length: 31 }, (_, i) => i + 1).map(day => (
                          <option key={day} value={day}>{day}</option>
                        ))}
                      </select>
                      <span className="text-sm text-glass-secondary">of each month</span>
                    </div>
                  )}
                  
                  {/* Nth weekday of month */}
                  {monthlyType === 'weekday' && (
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm text-glass-secondary">On the</span>
                      <select
                        value={monthlyWeek}
                        onChange={(e) => setMonthlyWeek(e.target.value as typeof monthlyWeek)}
                        className="form-input w-28"
                      >
                        {MONTH_WEEKS.map(week => (
                          <option key={week.value} value={week.value}>{week.label}</option>
                        ))}
                      </select>
                      <select
                        value={monthlyWeekday}
                        onChange={(e) => setMonthlyWeekday(Number(e.target.value))}
                        className="form-input w-28"
                      >
                        {WEEKDAYS.map(day => (
                          <option key={day.value} value={day.value}>
                            {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][day.value]}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              )}
              
              {/* End date for recurring events */}
              {recurrencePattern !== 'none' && (
                <div className="mt-4">
                  <label className="block text-sm font-medium text-glass mb-2">
                    End repeat (optional)
                  </label>
                  <input
                    type="date"
                    value={recurrenceEndDate}
                    onChange={(e) => setRecurrenceEndDate(e.target.value)}
                    min={startDate}
                    className="form-input w-full sm:w-auto"
                    placeholder="Never"
                  />
                  <p className="text-xs text-glass-muted mt-1">
                    Leave blank to repeat indefinitely
                  </p>
                </div>
              )}
            </div>

            {/* Links */}
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-glass mb-2">
                  <Link className="icon-sm inline mr-1" />
                  Shop URL
                </label>
                <input
                  type="url"
                  value={shopUrl}
                  onChange={(e) => setShopUrl(e.target.value)}
                  placeholder="https://yourshop.com"
                  className="form-input w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-glass mb-2">
                  Social/Live URL
                </label>
                <input
                  type="url"
                  value={socialUrl}
                  onChange={(e) => setSocialUrl(e.target.value)}
                  placeholder="Instagram Live link, etc."
                  className="form-input w-full"
                />
              </div>
            </div>

            {/* Discount info (for sales) */}
            {eventType === 'sale' && (
              <div>
                <label className="block text-sm font-medium text-glass mb-2">
                  <Tag className="icon-sm inline mr-1" />
                  Discount Details
                </label>
                <input
                  type="text"
                  value={discountInfo}
                  onChange={(e) => setDiscountInfo(e.target.value)}
                  placeholder="e.g., 20% off everything, Free shipping over $50"
                  className="form-input w-full"
                />
              </div>
            )}

            {/* Products teased (for releases/restocks) */}
            {(eventType === 'release' || eventType === 'restock') && (
              <div>
                <label className="block text-sm font-medium text-glass mb-2">
                  <Sparkles className="icon-sm inline mr-1" />
                  Products (teasers)
                </label>
                <div className="space-y-2">
                  {productsTeased.map((product, index) => (
                    <div key={index} className="flex gap-2">
                      <input
                        type="text"
                        value={product}
                        onChange={(e) => updateProductTeaser(index, e.target.value)}
                        placeholder="Product name..."
                        className="form-input flex-1"
                      />
                      {productsTeased.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeProductTeaser(index)}
                          className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg transition-default"
                        >
                          <Trash2 className="icon-sm" />
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addProductTeaser}
                    className="text-sm text-pink-400 hover:text-pink-300 flex items-center gap-1"
                  >
                    <Plus className="icon-sm" />
                    Add another product
                  </button>
                </div>
              </div>
            )}

            {/* Submit buttons */}
            <div className="flex gap-3 pt-4 border-t border-slate-700">
              <button
                type="button"
                onClick={onClose}
                className="btn-secondary flex-1"
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary flex-1"
                disabled={saving}
              >
                {saving ? (
                  <>
                    <span className="animate-spin inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                    {isEditMode ? 'Saving...' : 'Creating...'}
                  </>
                ) : (
                  isEditMode ? 'Save Changes' : 'Create Event'
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
